import { EditorState } from 'draft-js';

import { QTaskTag, TaskPriority, TaskType } from '@qwealth/qdata';

export const PAGE_MAIN_INFO = 'Main Info';
export const PAGE_MAIN_VIEW_WORKFLOW_TEMPLATE = 'View Workflow Template';
export const PAGE_ADD_WORKFLOW_TEMPLATE = 'Add Workflow Template';
export const PAGE_EDIT_WORKFLOW_TEMPLATE = 'Edit Workflow Template';
export const PAGE_ADD_TASK_TEMPLATE = 'Add Task Template';
export const PAGE_EDIT_TASK_TEMPLATE = 'Edit Task Template';

export const PAGE_OPTIONS = [
  PAGE_MAIN_INFO,
  PAGE_MAIN_VIEW_WORKFLOW_TEMPLATE,
  PAGE_ADD_WORKFLOW_TEMPLATE,
  PAGE_EDIT_WORKFLOW_TEMPLATE,
  PAGE_ADD_TASK_TEMPLATE,
  PAGE_EDIT_TASK_TEMPLATE,
] as const;
export type PageOptions = (typeof PAGE_OPTIONS)[number];

export const CRUMB_LABEL_WORKFLOW = 'Workflow';
export const CRUMB_LABEL_ADD_WORKFLOW = 'Add Workflow';
export const CRUMB_LABEL_EDIT_WORKFLOW = 'Edit Workflow';
export const CRUMB_LABEL_ADD_TASK = 'Add Task';
export const CRUMB_LABEL_EDIT_TASK = 'Edit Task';

export const CRUMB_OPTIONS = [
  CRUMB_LABEL_WORKFLOW,
  CRUMB_LABEL_ADD_WORKFLOW,
  CRUMB_LABEL_EDIT_WORKFLOW,
  CRUMB_LABEL_ADD_TASK,
  CRUMB_LABEL_EDIT_TASK,
] as const;
export type CrumbOptions = (typeof CRUMB_OPTIONS)[number];

export const DUE_DATE_OFFSET_PERIOD_DAYS = 'Days';
export const DUE_DATE_OFFSET_PERIOD_WEEKS = 'Weeks';
export const DUE_DATE_OFFSET_PERIOD_MONTHS = 'Months';

export const DUE_DATE_OFFSET_PERIOD_OPTIONS = [DUE_DATE_OFFSET_PERIOD_DAYS, DUE_DATE_OFFSET_PERIOD_WEEKS, DUE_DATE_OFFSET_PERIOD_MONTHS] as const;
export type DueDateOffsetPeriodOptions = (typeof DUE_DATE_OFFSET_PERIOD_OPTIONS)[number];

export const FROM_WORKFLOW_ASSIGNED_DATE = 'Workflow Assigned Date';
export const FROM_UNBLOCKED_GROUP_DATE = 'Unblocked Group Date';

export const TASK_DUE_DATE_OPTIONS = [FROM_WORKFLOW_ASSIGNED_DATE, FROM_UNBLOCKED_GROUP_DATE] as const;
export type TaskDueDateOptions = (typeof TASK_DUE_DATE_OPTIONS)[number];

export interface BaseTaskTemplate {
  readonly id?: number;
  temporaryUuid?: string;
  title: string;
  details: string;
  isInternal: 0 | 1;
  priority: TaskPriority;
  taskType: TaskType;
  dueDateOptions: TaskDueDateOptions;
  dueDateOffset: number;
  dueDateOffsetPeriod: DueDateOffsetPeriodOptions;
  assignee: string | null;
  tags: QTaskTag[];
  partnerId: number;
  iaCode: string | null;
  attachmentsToDelete?: string[];
}
export interface TaskTemplateFormValues extends Omit<BaseTaskTemplate, 'details' | 'tags' | 'partnerId' | 'iaCode'> {
  tagKeys: string[];
  editorState: EditorState;
  pending?: Array<File>;
}

export interface WorkflowTemplateGroup {
  tasksGroup: BaseTaskTemplate[];
}
export interface NewWorkflowTemplate {
  name: string;
  description: string;
  enableDueDate: boolean;
  dueDateOffset?: number;
  dueDateOffsetPeriod?: DueDateOffsetPeriodOptions;
  tags: QTaskTag[];
  partnerId: number;
  iaCode: string | null;
  groups: WorkflowTemplateGroup[];
}
export interface WorkflowTemplate extends NewWorkflowTemplate {
  readonly id: number;
}

export interface WorkflowTemplateFormValues extends Omit<NewWorkflowTemplate, 'description' | 'tags' | 'partnerId' | 'iaCode' | 'groups'> {
  tagKeys: string[];
  editorState: EditorState;
}

export interface TaskTemplatePayload {
  groupIndex: number;
  taskTemplate: BaseTaskTemplate;
}

export interface RemoveTaskTemplatePayload {
  id?: number;
  temporaryUuid?: string;
  groupIndex: number;
}

export interface WorkflowGroup {
  groupIndex: number;
  tasks: BaseTaskTemplate[];
}

export type TaskTemplateActions = {
  updateTaskTemplate: (groupIndex: number, task: BaseTaskTemplate) => void;
  createTaskTemplate: (groupIndex: number) => void;
  removeTaskTemplate: (groupIndex: number, id?: number, temporaryUuid?: string) => void;
};

export type TaskTemplatesPendingFilesManagement = {
  changePendingFilesOnStorageByTaskTemplateId: (id: number | string, files: File[]) => void;
  getPendingFilesFromStorageByTaskTemplateId: (id: number | string) => File[];
  getAllTaskTemplatesPendingFilesFromStorage: () => Record<string, File[]>;
  deletePendingFilesFromStorageByTaskTemplateId: (id: number | string) => void;
  deleteMultipleTaskTemplatesPendingFilesFromStorages: (ids: (number | string)[]) => void;
  resetStorage: () => void;
};

export interface SelectedTaskTemplate {
  groupIndex: number;
  taskTemplate: BaseTaskTemplate | undefined;
}

export interface WorkflowTemplateFilters {
  partnerId: number;
  iaCode?: string | null;
}
